import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BackendService } from './backend.service';
import { StatsSummary } from '../models/stats.model';

/**
 * Owns the caller's session/time-saved stats for the welcome stat strip and
 * the user-menu "time saved" card.
 *
 * Reads GET /agenthome/me/stats (qualified sessions + estimated minutes
 * saved, valued at each agent's rate on the session's date — see
 * stats.model.ts). Mirrors UsageService's shape: signal state, single-flight
 * refresh, fail-soft (widgets show nothing rather than blocking chat).
 * The server caches per-user for 60s, so refresh-per-turn stays cheap.
 */
@Injectable({ providedIn: 'root' })
export class StatsService {
  private readonly http = inject(HttpClient);
  private readonly backend = inject(BackendService);

  readonly stats = signal<StatsSummary | null>(null);
  readonly loading = signal(false);
  readonly errored = signal(false);

  private inFlight = false;

  /** Single-flight refresh; only shows the loading state on the first fetch. */
  refresh(): void {
    if (this.inFlight) return;
    this.inFlight = true;
    if (this.stats() === null) this.loading.set(true);

    const url = this.backend.createAgentsUrl('agenthome/me/stats');
    this.http.get<{ success: boolean; body: StatsSummary | null }>(url).subscribe({
      next: res => {
        this.stats.set(res?.body ?? null);
        this.errored.set(false);
        this.loading.set(false);
        this.inFlight = false;
      },
      error: () => {
        this.errored.set(true);
        this.loading.set(false);
        this.inFlight = false;
      },
    });
  }

  /** Clear on logout so stats never leak to the next user in the same tab. */
  reset(): void {
    this.stats.set(null);
    this.errored.set(false);
    this.loading.set(false);
  }
}
